/**
 * Papan kerja harian — beban per tahap per hari, LINTAS SEMUA WO (K50).
 *
 * ⛔ TIGA HAL YANG DITAMPILKAN LAYAR INI SENGAJA, DAN KETIGANYA MUDAH
 * DIANGGAP KEBISINGAN:
 *
 *  1. **Tahap yang MENGANGGUR tetap tampil** dengan angka nol. Bagian yang
 *     kosong adalah informasi yang sama pentingnya dengan yang kebanjiran —
 *     menyembunyikannya membuat kapasitas menganggur nol pernah terlihat.
 *
 *  2. **KELEBIHAN ditunjukkan, bukan ditolak.** Papan MEMBACA keadaan; yang
 *     memutuskan menggeser pekerjaan adalah manusia yang melihat angkanya.
 *
 *  3. **WO yang BELUM DIJADWALKAN dilaporkan terpisah.** Bebannya nyata dan
 *     nol terhitung di tahap mana pun — papan yang menyembunyikannya
 *     melaporkan kapasitas lebih longgar dari kenyataan, dan angka "sisa 150"
 *     mengundang pekerjaan baru yang nol muat.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  apiErrorMessage,
  getPapanKerja,
  getRegions,
  kirimTugasHarian,
  labelTahap,
  LABEL_ALASAN_GESER,
  pratinjauGeser,
  simpanGeser,
} from "@/lib/papanKerjaApi";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import PanelKesehatanKiriman from "@/components/etams/PanelKesehatanKiriman";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { tanggalLokalISO } from "../lib/format";
import { labelPaket } from "../lib/faKonfigurasi";

const hariIni = () => tanggalLokalISO();

// Peran yang boleh menggeser (K172 G5). OWNER SENGAJA NOL DI SINI — C1:
// Owner menggeser hanya lewat "Bertindak Atas Nama" Kepala Cabang (K140);
// saat itu `profile.role.code` sudah KEPALA_CABANG. Backend tetap yang
// menjaga; ini hanya menentukan tombolnya tampil atau tidak (B1).
const PERAN_GESER = ["ADMIN_PRODUKSI", "KEPALA_CABANG"];

/** Galat backend verbatim; B12 untuk tahap domino menyebut tahapnya (`detail.tahap`). */
function pesanGalatGeser(e) {
  const d = e?.response?.data?.detail;
  const pesan = apiErrorMessage(e);
  if (d && typeof d === "object" && d.tahap) return `${labelTahap(d.tahap)} — ${pesan}`;
  return pesan;
}

/**
 * Dialog geser satu tahap (K172 B2–B10). Seluruh teksnya milik K172 §6 —
 * jangan diparafrase. Guard ada di backend; galat backend tampil verbatim.
 *
 * Pratinjau dipanggil begitu tanggal dipilih (alasan boleh masih kosong):
 * yang ditunjukkan adalah tahap mana saja yang ikut mundur (B6) dan apakah
 * tanggal itu melewati deadline customer (B9/B10) — SEBELUM disimpan (G1).
 */
function DialogGeser({ tugas, stageCode, onTutup, onSelesai }) {
  const [ke, setKe] = useState("");
  const [alasan, setAlasan] = useState("");
  const [teks, setTeks] = useState("");
  const [paham, setPaham] = useState(false);
  const [pratinjau, setPratinjau] = useState(null);
  const [galat, setGalat] = useState("");
  const [sibuk, setSibuk] = useState(false);

  const terbuka = Boolean(tugas);
  const dari = tugas?.tanggal_kerja;
  const woId = tugas?.wo_id;
  const woStageId = tugas?.wo_stage_id;

  useEffect(() => {
    // Dialog dibuka untuk tugas lain → isian bersih. Nilai lama yang
    // menggantung akan menyimpan geser tugas A dengan tanggal yang dipilih
    // untuk tugas B.
    setKe(""); setAlasan(""); setTeks(""); setPaham(false);
    setPratinjau(null); setGalat("");
  }, [woStageId]);

  useEffect(() => {
    if (!terbuka || !ke || ke === dari) { setPratinjau(null); return undefined; }
    let batal = false;
    setGalat("");
    pratinjauGeser(woId, stageCode, { ke_tanggal: ke })
      .then(({ data }) => { if (!batal) setPratinjau(data); })
      .catch((e) => { if (!batal) { setPratinjau(null); setGalat(pesanGalatGeser(e)); } });
    return () => { batal = true; };
  }, [terbuka, ke, dari, woId, stageCode]);

  if (!terbuka) return null;

  const melewati = Boolean(pratinjau?.melewati_deadline);
  const lainnyaPendek = alasan === "LAINNYA" && teks.trim().length < 10;
  const bolehSimpan = Boolean(ke) && ke !== dari && Boolean(alasan) && !lainnyaPendek
    && Boolean(pratinjau) && (!melewati || paham) && !sibuk;

  const onSimpan = async () => {
    setSibuk(true);
    setGalat("");
    try {
      const { data } = await simpanGeser(woId, stageCode, {
        ke_tanggal: ke, alasan_kode: alasan,
        alasan_teks: teks.trim() || null, paham_telat: paham,
      });
      // B16 — n = 0: kalimat pertama saja.
      const n = (data.ikut_domino || []).length;
      const kalimat = `Tahap ${labelTahap(stageCode)} digeser ke ${formatDate(data.ke_tanggal)}.`;
      toast.success(n > 0 ? `${kalimat} ${n} tahap ikut mundur.` : kalimat);
      onSelesai();
    } catch (e) {
      setGalat(pesanGalatGeser(e));
    } finally {
      setSibuk(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !sibuk) onTutup(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          {/* B2 */}
          <DialogTitle>Geser tahap {labelTahap(stageCode)} · {tugas.wo_number}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            {/* B3 */}
            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Dari</span>
              <div className="rounded-lg border border-input bg-muted/40 px-3 py-2 tabular-nums">
                {formatDate(dari)}
              </div>
            </div>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Ke tanggal</span>
              <input
                type="date"
                className="w-full rounded-lg border border-input bg-background px-3 py-2"
                value={ke}
                onChange={(e) => { setKe(e.target.value); setPaham(false); }}
                data-testid="geser-ke-tanggal"
              />
            </label>
          </div>

          {/* A3 — alasan G3 */}
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(LABEL_ALASAN_GESER).map(([kode, label]) => (
              <label
                key={kode}
                className={
                  "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 " +
                  (alasan === kode ? "border-primary bg-primary/10" : "border-input")
                }
              >
                <input
                  type="radio"
                  name="alasan-geser"
                  value={kode}
                  checked={alasan === kode}
                  onChange={() => setAlasan(kode)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {alasan === "LAINNYA" && (
            <textarea
              className="w-full rounded-lg border border-input bg-background px-3 py-2"
              rows={2}
              placeholder="Tulis sebabnya, minimal 10 huruf"
              value={teks}
              onChange={(e) => setTeks(e.target.value)}
              data-testid="geser-alasan-teks"
            />
          )}

          {/* B6 — pratinjau domino G1 */}
          {pratinjau && (
            <p className="rounded-lg border border-border bg-muted/40 px-3 py-2">
              {pratinjau.ikut_domino?.length > 0
                ? "Ikut mundur: " + pratinjau.ikut_domino
                  .map((x) => `${labelTahap(x.stage_code)} → ${formatDate(x.ke)}`)
                  .join(", ")
                : "Tidak ada tahap lain yang ikut"}
            </p>
          )}

          {/* B9 + B10 — peringatan tegas G4; hanya Kepala Cabang yang sampai sini */}
          {melewati && (
            <div className="rounded-lg border border-danger-soft-border bg-danger-soft px-3 py-2 text-danger-soft-foreground">
              <p className="font-medium">
                ⚠ Melewati deadline customer ({formatDate(pratinjau.finish_date)}). Deadline TIDAK
                berubah — pesanan ini akan telat. Ubah deadline hanya lewat Special Date Request.
              </p>
              {/* K190 butir 5 (BUSINESS-01 (c)): sebabnya disebut — tahap yang digeser,
                  atau ujung rantai jadwal yang jatuh sesudah deadline. */}
              {pratinjau.melewati_proyeksi && !pratinjau.melewati_tahap && (
                <p className="mt-1 text-sm" data-testid="geser-sebab-proyeksi">
                  Tahap yang digeser masih sebelum deadline, tetapi ujung rantai jadwal
                  ({formatDate(pratinjau.selesai_jadwal_baru)}) jatuh sesudah deadline.
                </p>
              )}
              <label className="mt-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={paham}
                  onChange={(e) => setPaham(e.target.checked)}
                  data-testid="geser-paham-telat"
                />
                <span>Saya paham pesanan ini akan telat</span>
              </label>
            </div>
          )}

          {galat && (
            <div role="alert" className="rounded-lg border border-danger-soft-border bg-danger-soft px-3 py-2 text-danger-soft-foreground">
              {galat}
            </div>
          )}
        </div>

        <DialogFooter>
          {/* B8 / B7 */}
          <button
            type="button"
            onClick={onTutup}
            disabled={sibuk}
            className="rounded-lg border border-input px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={onSimpan}
            disabled={!bolehSimpan}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            data-testid="geser-simpan"
          >
            {sibuk ? "Menyimpan…" : "Simpan geser"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Bar({ terpakai, kapasitas }) {
  if (!kapasitas) return null;
  const pakai = Math.min(terpakai / kapasitas, 1) * 100;
  const lebih = terpakai > kapasitas;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={`h-full rounded-full ${lebih ? "bg-danger" : "bg-success"}`}
        style={{ width: `${pakai}%` }}
      />
    </div>
  );
}

function KartuTahap({ tahap, bolehGeser, onGeser }) {
  const { kapasitas, terpakai, sisa, kelebihan, tugas } = tahap;
  const kosong = tugas.length === 0;
  return (
    <div
      className={
        "rounded-xl border bg-card p-4 " +
        (kelebihan > 0 ? "border-danger-soft-border" : "border-border")
      }
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="font-semibold text-foreground">{labelTahap(tahap.stage_code)}</h3>
        <span className="tabular-nums text-sm text-muted-foreground">
          {terpakai} / {kapasitas ?? "—"}
        </span>
      </div>

      <Bar terpakai={terpakai} kapasitas={kapasitas} />

      <p className="mt-2 text-sm">
        {kelebihan > 0 ? (
          <span className="font-medium text-danger-soft-foreground">
            Kelebihan {kelebihan} atasan — perlu digeser
          </span>
        ) : kosong ? (
          /* ⚠ Kalimat ini SENGAJA ada. Bagian yang menganggur adalah
             informasi, bukan kekosongan yang perlu disembunyikan. */
          <span className="text-muted-foreground">Tidak ada pekerjaan hari ini</span>
        ) : (
          <span className="text-muted-foreground">Sisa {sisa} atasan</span>
        )}
      </p>

      {!kosong && (
        <ul className="mt-3 space-y-2 border-t border-border pt-3">
          {tugas.map((t) => (
            <li key={t.wo_stage_id || t.wo_id} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0">
                <span className="font-medium text-foreground">{t.wo_number}</span>
                {/* K233 (BUSINESS-111): nama tim untuk mencocokkan ikatan barang — dikirim server hanya ke peran yang boleh. */}
                {t.nama_tim && <span className="ml-2 text-xs text-foreground" data-testid="papan-nama-tim">{t.nama_tim}</span>}
                {t.tunggakan && (
                  /* ⛔ K170 V5b/V8: tahap yang tanggalnya lewat dan belum
                     selesai tampil di papan HARI INI, di depan semua tugas
                     hari itu (urutannya dari backend). Tanggal yang ditulis
                     adalah jadwal ASLI yang terlewat — bukan hari ini. */
                  <span className="ml-2 rounded border border-danger-soft-border bg-danger-soft px-1.5 py-0.5 text-xs font-semibold text-danger-soft-foreground">
                    Tunggakan · jadwal {formatDate(t.tanggal_kerja)}
                  </span>
                )}
                {t.terancam_deadline && (
                  /* ⛔ K50: yang terancam deadline naik ke depan APA PUN
                     paketnya. Penandanya di baris yang sama, bukan di
                     keterangan bawah — yang membaca daftar ini sedang
                     memutuskan urutan kerja. */
                  <span className="ml-2 rounded border border-warning-soft-border bg-warning-soft px-1.5 py-0.5 text-xs font-semibold text-warning-soft-foreground">
                    Terancam telat
                  </span>
                )}
                {t.jumlah_geser > 0 && (
                  /* A1 — K172 G8: tunggakan yang digeser keluar dari
                     kelompok tunggakan, tapi jejaknya tetap terbaca. */
                  <span className="ml-2 rounded border border-info-soft-border bg-info-soft px-1.5 py-0.5 text-xs font-semibold text-info-soft-foreground">
                    digeser dari tanggal {formatDate(t.digeser_dari)}, {t.jumlah_geser} kali
                  </span>
                )}
                {t.lewat_deadline && (
                  /* B19 — geser yang melewati deadline customer (hanya KC). */
                  <span className="ml-1 rounded border border-danger-soft-border bg-danger-soft px-1.5 py-0.5 text-xs font-semibold text-danger-soft-foreground">
                    lewat deadline
                  </span>
                )}
                <span className="ml-2 text-xs text-muted-foreground">{labelPaket(t.paket)}</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="tabular-nums text-muted-foreground">{t.qty_atasan}</span>
                {/* B1 — hanya peran G5 + tahap PENDING (G7). */}
                {bolehGeser && t.status === "PENDING" && (
                  <button
                    type="button"
                    onClick={() => onGeser(t)}
                    className="rounded border border-input px-2 py-0.5 text-xs font-medium hover:bg-muted"
                    data-testid={`geser-${t.wo_stage_id}`}
                  >
                    Geser
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function PapanKerja() {
  const { profile } = useAuth();
  const [tanggal, setTanggal] = useState(hariIni());
  const [data, setData] = useState(null);
  const [muat, setMuat] = useState(true);
  const [galat, setGalat] = useState("");
  const [kirim, setKirim] = useState(false);
  const [ringkas, setRingkas] = useState(null);
  // K172 — tugas yang sedang digeser: { tugas, stageCode } atau null.
  const [geser, setGeser] = useState(null);
  const bolehGeser = PERAN_GESER.includes(profile?.role?.code);

  // ⛔ K50: SATU PAPAN PER REGION. Peran yang terikat satu wilayah nol perlu
  //    memilih — backend memaksanya ke wilayahnya sendiri apa pun yang dikirim
  //    layar ini. Yang BOLEH memilih (Owner) WAJIB menyebutkannya, dan sampai
  //    30 Agustus 2026 layar ini nol menyediakan caranya: ia hanya menampilkan
  //    "Sebutkan wilayah yang ingin dilihat" lalu berhenti di situ.
  const regionSendiri = profile?.region_id || null;
  const perluPilih = !regionSendiri;
  const [regions, setRegions] = useState([]);
  const [pilihRegion, setPilihRegion] = useState("");
  const regionId = regionSendiri || pilihRegion || undefined;

  useEffect(() => {
    if (!perluPilih) return;
    getRegions()
      .then(({ data }) => {
        const daftar = data || [];
        setRegions(daftar);
        // Satu wilayah = nol ada yang perlu dipilih. Memaksa memilih dari
        // daftar berisi satu baris cuma menambah ketukan tanpa menambah
        // keputusan.
        if (daftar.length === 1) setPilihRegion(daftar[0].id);
      })
      .catch((e) => setGalat(apiErrorMessage(e)));
  }, [perluPilih]);

  const muatData = useCallback(async () => {
    // ⚠ Berhenti SEBELUM memanggil, bukan sesudah ditolak. Permintaan tanpa
    //   wilayah dijawab 400 oleh backend, dan menampilkannya sebagai kotak
    //   merah membuat keadaan "belum memilih" terlihat seperti kerusakan.
    if (perluPilih && !pilihRegion) {
      setData(null);
      setGalat("");
      setMuat(false);
      return;
    }
    setMuat(true);
    setGalat("");
    try {
      const { data: d } = await getPapanKerja({ tanggal, region_id: regionId });
      setData(d);
    } catch (e) {
      setGalat(apiErrorMessage(e));
      setData(null);
    } finally {
      setMuat(false);
    }
  }, [tanggal, regionId, perluPilih, pilihRegion]);

  useEffect(() => {
    muatData();
  }, [muatData]);

  const onKirim = async () => {
    setKirim(true);
    setGalat("");
    setRingkas(null);
    try {
      const { data: r } = await kirimTugasHarian({ tanggal, region_id: regionId });
      setRingkas(r);
      await muatData();
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setKirim(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Papan Kerja Harian</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Beban tiap tahap hari ini, dijumlahkan dari semua WO di
            wilayah Anda.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {perluPilih && (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-foreground">Wilayah</span>
              <select
                className="rounded-lg border border-input px-3 py-2 text-sm"
                value={pilihRegion}
                onChange={(e) => setPilihRegion(e.target.value)}
              >
                <option value="">Pilih wilayah…</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </label>
          )}
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">Tanggal</span>
            <input
              type="date"
              className="rounded-lg border border-input px-3 py-2 text-sm"
              value={tanggal}
              onChange={(e) => setTanggal(e.target.value)}
            />
          </label>
          {/* ⛔ Ikut mati saat wilayah belum dipilih. Tombol ini MENGIRIM tugas
              ke orang sungguhan lewat Telegram — membiarkannya hidup di atas
              papan yang nol punya wilayah berarti satu ketukan salah mengirim
              kabar ke wilayah yang nol dimaksud, dan pesan Telegram nol bisa
              ditarik kembali. */}
          <button
            type="button"
            onClick={onKirim}
            disabled={kirim || muat || (perluPilih && !pilihRegion)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {kirim ? "Mengirim…" : "Kirim tugas via Telegram"}
          </button>
        </div>
      </div>

      {galat && (
        <div role="alert" className="rounded-lg border border-danger-soft-border bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground">
          {galat}
        </div>
      )}

      {/* ⛔ RINGKASAN KIRIMAN MENAMPILKAN KEGAGALANNYA, bukan cuma jumlah yang
          berhasil. Mesin yang diam saat nol ada penerima terlihat persis sama
          dengan mesin yang berhasil mengirim semuanya. */}
      {ringkas && (
        <div className="rounded-xl border border-border bg-card p-4 text-sm">
          <p className="font-medium text-foreground">
            {ringkas.terkirim} tugas terkirim
            {ringkas.dilewati ? `, ${ringkas.dilewati} sudah pernah dikirim` : ""}
          </p>
          {ringkas.belum_daftar > 0 && (
            <p className="mt-2 text-warning-soft-foreground">
              {ringkas.belum_daftar} tugas <strong>belum terkirim</strong> — orangnya
              belum menekan Start di bot Telegram. Terbitkan tautan pendaftaran
              dari layar Personil.
            </p>
          )}
          {ringkas.tanpa_penerima?.length > 0 && (
            <p className="mt-2 text-danger-soft-foreground">
              Tidak ada yang ditugaskan ke tahap{" "}
              <strong>{ringkas.tanpa_penerima.map(labelTahap).join(", ")}</strong> —
              pekerjaannya tetap ada, kabarnya yang hilang.
            </p>
          )}
          {ringkas.tidak_hadir?.length > 0 && (
            <p className="mt-2 text-warning-soft-foreground">
              Tahap{" "}
              <strong>
                {ringkas.tidak_hadir.map((x) => labelTahap(x.tahap)).join(", ")}
              </strong>{" "}
              belum ada yang tercatat hadir. Admin Produksi sudah dikabari.
            </p>
          )}
          {ringkas.gagal?.length > 0 && (
            <p className="mt-2 text-danger-soft-foreground">
              {ringkas.gagal.length} tugas gagal terkirim: {ringkas.gagal[0].alasan}
            </p>
          )}
        </div>
      )}

      {muat && <p className="text-sm text-muted-foreground">Memuat…</p>}

      {/* ⚠ Keadaan "belum memilih" punya layarnya sendiri, dan ia SENGAJA
          bukan kotak merah. Layar kosong tanpa kalimat membuat orang menebak
          apakah wilayahnya memang nol punya pekerjaan atau aplikasinya rusak —
          dua kesimpulan yang jauh berbeda akibatnya. */}
      {!muat && perluPilih && !pilihRegion && (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Pilih wilayah lebih dulu — tiap wilayah punya papan kerjanya sendiri,
          dan angkanya tidak boleh dijumlahkan.
        </div>
      )}

      {!muat && data && (
        <>
          {/* ⛔ Dilaporkan TERPISAH, bukan disembunyikan — lihat catatan
              kepala berkas. */}
          {data.belum_terjadwal?.length > 0 && (
            <div className="rounded-xl border border-warning-soft-border bg-warning-soft p-4 text-sm text-warning-soft-foreground">
              <p className="font-semibold">
                {data.belum_terjadwal.length} WO belum dijadwalkan
              </p>
              <p className="mt-1">
                Bebannya nyata tapi belum terhitung di tahap mana pun, jadi angka
                sisa kapasitas di bawah <strong>lebih longgar dari kenyataan</strong>.
              </p>
              <ul className="mt-2 space-y-0.5">
                {data.belum_terjadwal.map((w) => (
                  <li key={w.wo_id} className="tabular-nums">
                    {w.wo_number}{w.nama_tim ? ` · ${w.nama_tim}` : ""} · {labelPaket(w.paket)} · {w.qty_atasan} atasan
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* K135 LAPIS 3 — papan kesehatan kiriman (sensus pintu 23 Sep 2026: rutenya dulu tanpa layar). */}
          <PanelKesehatanKiriman />
          {/* ⛔ K135 LAPIS 2 — JALUR KEDUA. Tugas yang kabarnya nol sampai
              tampil DI SINI, di layar orang yang bisa menelepon — bukan cuma
              di log. Nomor teleponnya ikut, supaya tindakannya satu ketukan
              dari sini alih-alih pencarian di layar lain. */}
          {data.belum_tersampaikan?.length > 0 && (
            <div className="rounded-xl border border-danger-soft-border bg-danger-soft p-4 text-sm text-danger-soft-foreground">
              <p className="font-semibold">
                {data.belum_tersampaikan.length} tugas hari ini KABARNYA BELUM SAMPAI
              </p>
              <p className="mt-1">
                Pekerjaannya tetap ada — yang hilang cuma kabarnya. Hubungi
                orangnya langsung.
              </p>
              <ul className="mt-2 space-y-1">
                {data.belum_tersampaikan.map((t) => (
                  <li key={t.id}>
                    <span className="font-medium">{labelTahap(t.stage_code)}</span>
                    {" · "}
                    {t.nama || "penerima tidak dikenali"}
                    {t.telepon ? ` · ${t.telepon}` : ""}
                    {" — "}
                    {t.status === "BELUM_DAFTAR"
                      ? "belum menekan Start di bot Telegram"
                      : `gagal ${t.percobaan}× — ${t.alasan || "sebab tidak tercatat"}`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {data.tahap.map((t) => (
              <KartuTahap
                key={t.stage_code}
                tahap={t}
                bolehGeser={bolehGeser}
                onGeser={(tugas) => setGeser({ tugas, stageCode: t.stage_code })}
              />
            ))}
          </div>
        </>
      )}

      <DialogGeser
        tugas={geser?.tugas || null}
        stageCode={geser?.stageCode}
        onTutup={() => setGeser(null)}
        onSelesai={() => { setGeser(null); muatData(); }}
      />
    </div>
  );
}
